import { useTable, CreateButton } from '@refinedev/antd';
import { ToolList, embeddedTableProps } from '../../components/ToolList';
import { Button, Space, Table, Tooltip } from 'antd';

import {
  IResourceComponentsProps,
  useGetIdentity,
  useGetLocale,
  useTranslate,
} from '@refinedev/core';

import { dbCols, UserRole } from '@cc/schema';
import { format } from 'date-fns';
import {
  ClientName,
  ShowNamesButton,
  clientFilterText,
  useClientNamesVisibility,
} from '../../components/clientNames';
import { useClientRoster } from '../../hooks/client-options';
import { Conceptualization } from '../../schema/be-graphql-generated';
import { IUser } from '../../providers';
import { Link, useNavigate } from 'react-router-dom';
import { EditOutlined } from '@ant-design/icons';
import { useContext, useEffect } from 'react';
import { TherapistActivityReactContext } from '../../App';
import { useActor } from '@xstate/react';
import { TherapistActivityService } from '../../machines/therapistActivityMachine';
import { useActivityTracking } from '../../hooks/use-activity-tracking';
import useStyles from './styles.style';

/**
 * E57 · EMBEDDED on the client page with the client FIXED (`fixedClientId` — registry decision `per-client-views`):
 * the same list, filtered to that client whatever the header bar's filter says, with no client column; Create opens the
 * create page with `?clientId=` (Q-e57g1-2 A rule 4); the page that hosts it records the RTM page view (rule 2), so
 * the list's own tracking is off. Standalone (no `fixedClientId`) it behaves exactly as before.
 */
export const ConceptualizationsList: React.FC<
  IResourceComponentsProps & { fixedClientId?: string }
> = ({ fixedClientId }) => {
  const translate = useTranslate();

  const locale = useGetLocale();

  const currentLocale = locale();

  const { styles } = useStyles();

  const therapistActivityService = useContext(TherapistActivityReactContext);

  const [therapistActivityState, therapistActivitySend] = useActor(
    therapistActivityService as TherapistActivityService,
  );
  // E57 — the client page fixes the client; otherwise the header bar's filter
  const clientFilterId = fixedClientId || therapistActivityState?.context?.filterClientId;
  const navigateTo = useNavigate();

  const {
    data: identity,
    remove,
    refetch: refetchIdentity,
  } = useGetIdentity<IUser>();

  // @ts-ignore
  const { tableProps, setFilters } = useTable<Conceptualization>({
    // E57: the resource is named, never inferred from the route (embedded on the client page the route's is `clients`)
    resource: 'conceptualizations',
    queryOptions: {
      select: (data) => {
        // @ts-ignore
        return { data: data.data.data, total: data.total };
      },
    },

    meta: {
      operation: 'conceptualizations',
      fields: [
        {
          data: [
            '_id',
            'userId',
            'createdAt',
            'updatedAt',
            'dob',
            'gender',
            'education',
            'maritalStatus',
            'clientProfileOther',
            'negativePastExperience',
            'triggerEvent',
            'geneticPredisposition',
            'deepBeliefs',
            'triggers',
            'problems',
            'lifeRules',
            'goals',
            'resources',
          ],
        },
        'count',
      ],
    },

    filters: {
      initial: clientFilterId
        ? [
            {
              field: 'userId',
              operator: 'eq',
              value: clientFilterId,
            },
          ]
        : [],
    },

    sorters: {
      initial: [
        {
          field: 'updatedAt',
          order: 'desc',
        },
      ],
    },
  });

  useEffect(() => {
    setFilters(
      clientFilterId
        ? [
            {
              field: 'userId',
              operator: 'eq',
              value: clientFilterId,
            },
          ]
        : [],
      'replace',
    );
  }, [clientFilterId]);

  // the WHOLE practice roster (P011) — the client column, the client filter and the edit gate below
  // all have to be able to name the owner of every row this table can render
  // Q-mt-3b A: "id · initials", full name on hover; Show names for 60 s
  const names = useClientNamesVisibility();
  const { clients } = useClientRoster();

  useActivityTracking(dbCols.conceptualizations, fixedClientId, !fixedClientId);

  return (
    <ToolList
      fixedClientId={fixedClientId}
      tableProps={tableProps}
      breadcrumb={fixedClientId ? false : undefined}
      title={translate('pages.conceptualizations.conceptualizations')}
      headerProps={{
        extra: (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {fixedClientId ? null : (
              <ShowNamesButton
                visible={names.visible}
                onToggle={names.toggle}
                testId="cc-conceptualization-toggle-names"
              />
            )}
            {/* E57 — "Conceptualizations (visible Create added)": a Create that keeps the fixed client (?clientId=) */}
            <CreateButton
              data-testid="create-conceptualization-button"
              onClick={() =>
                navigateTo(
                  fixedClientId
                    ? `/tools/conceptualizations/create?clientId=${encodeURIComponent(fixedClientId)}`
                    : '/tools/conceptualizations/create',
                )
              }
            />
          </div>
        ),
      }}
    >
      <Table
        {...embeddedTableProps(fixedClientId)}
        {...tableProps}
        rowKey="_id"
        onChange={(pagination, filters, sorter, extra) => {
          // Note: The data we get from Ant.design filters does not match the
          // react refine one. Currently it works fine if I simply silence it the way below.
          // TOOD: Find a way to convert the data to match the react refine one and fix it better
          // @ts-ignore
          tableProps?.onChange?.(pagination, null, sorter, extra);
        }}
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
        }}
        data-testid="conceptualizations-list"
      >
        {fixedClientId ? null : (
        <Table.Column
          key="_id"
          dataIndex="userId"
          title={translate('pages.conceptualizations.userId')}
          sorter
          filterSearch
          // @ts-ignore
          onFilter={(value: string, record: Conceptualization) => {
            // setFilters([
            //   {
            //     field: 'userId',
            //     operator: 'eq',
            //     value,
            //   },
            // ]);
            return record.userId === value;
          }}
          filters={clients.map((c) => {
            return {
              text: clientFilterText(c, names.visible),
              value: c._id,
            };
          })}
          render={(value, record) => {
            const client = clients.find((c) => c._id === value);

            return (
              <ClientName
                id={value}
                firstName={client?.firstName}
                lastName={client?.lastName}
                visible={names.visible}
              />
            );
          }}
        />
        )}
        <Table.Column
          key="createdAt"
          dataIndex="createdAt"
          title={translate('resourcesCommon.createdAt')}
          sorter
          render={(text, record, indx) => {
            if (!text) return undefined;
            const date = new Date(text);
            return format(
              date,
              currentLocale?.startsWith('ru')
                ? 'yyyy-MM-dd HH:mm:ss'
                : 'yyyy-MM-dd hh:mm:ss aaa',
            );
          }}
        />
        <Table.Column
          key="updatedAt"
          dataIndex="updatedAt"
          title={translate('resourcesCommon.updatedAt')}
          sorter
          render={(text, record, indx) => {
            if (!text) return undefined;
            const date = new Date(text);
            return format(
              date,
              currentLocale?.startsWith('ru')
                ? 'yyyy-MM-dd HH:mm:ss'
                : 'yyyy-MM-dd hh:mm:ss aaa',
            );
          }}
        />
        <Table.Column<Conceptualization>
          title={translate('resourcesCommon.actions')}
          dataIndex="actions"
          render={(_, record) => {
            const client = clients.find((c) => c._id === record.userId);

            const hasPremiumAccess = client?.accessLevel;

            const canEdit =
              client?.access?.allowEdit ||
              (client?.access?.allowAdminEdit &&
                identity?.db?.role === UserRole.THERAPIST_ADMIN);

            return (
              <Space>
                {canEdit && (
                  <Tooltip
                    title={translate(
                      hasPremiumAccess
                        ? 'resourcesCommon.edit'
                        : 'clients.thoughtsTestDisabledTip',
                    )}
                  >
                    <Link
                      to={`/tools/conceptualizations/create?clientId=${record.userId}`}
                    >
                      <Button
                        icon={<EditOutlined />}
                        disabled={!hasPremiumAccess || !canEdit}
                        data-testid="edit-conceptualization-button"
                      />
                    </Link>
                  </Tooltip>
                )}
              </Space>
            );
          }}
        />
      </Table>
    </ToolList>
  );
};
